/**
 * Recharge Subscription API Helper
 * Provides methods to interact with Recharge Storefront API
 */

class RechargeSubscriptionAPI {
  constructor() {
    this.rechargeClient = null;
    this.initialized = false;
    this.storeIdentifier = null;
  }

  /**
   * Initialize Recharge client
   * @param {Object} config - Configuration object
   * @param {string} config.storeIdentifier - Store domain
   * @param {string} config.storefrontAccessToken - Storefront API token
   */
  init(config) {
    if (typeof recharge === 'undefined') {
      console.error('Recharge SDK not loaded');
      return false;
    }

    try {
      recharge.init({
        storeIdentifier: config.storeIdentifier,
        storefrontAccessToken: config.storefrontAccessToken,
        appName: config.appName || 'subscription-widget',
        appVersion: config.appVersion || '1.0.0'
      });

      this.rechargeClient = recharge;
      this.initialized = true;
      this.storeIdentifier = config.storeIdentifier;

      return true;
    } catch (error) {
      console.error('Error initializing Recharge:', error);
      return false;
    }
  }

  /**
   * Get subscription options for a product
   * @param {string|number} productId - Shopify product ID
   * @returns {Promise<Object>} Product subscription data
   */
  async getProductSubscriptionOptions(productId) {
    if (!this.initialized) {
      throw new Error('Recharge API not initialized');
    }

    try {
      const response = await this.rechargeClient.products.get(productId);
      return response;
    } catch (error) {
      console.error('Error fetching product subscription options:', error);
      throw error;
    }
  }

  /**
   * Get selling plan for a variant
   * @param {string|number} variantId - Shopify variant ID
   * @returns {Promise<Array>} Selling plans
   */
  async getVariantSellingPlans(variantId) {
    if (!this.initialized) {
      throw new Error('Recharge API not initialized');
    }

    try {
      // Fetch variant data from Shopify Storefront API
      const response = await fetch(`/products/${variantId}.js`);
      const variantData = await response.json();

      return variantData.selling_plan_allocations || [];
    } catch (error) {
      console.error('Error fetching variant selling plans:', error);
      throw error;
    }
  }

  /**
   * Calculate subscription price with discount
   * @param {number} basePrice - Base product price in cents
   * @param {Object} sellingPlan - Selling plan object
   * @returns {Object} Price breakdown
   */
  calculateSubscriptionPrice(basePrice, sellingPlan) {
    if (!sellingPlan || !sellingPlan.price_adjustments) {
      return {
        price: basePrice,
        compareAtPrice: null,
        discount: 0,
        discountPercentage: 0
      };
    }

    const adjustment = sellingPlan.price_adjustments[0];
    let finalPrice = basePrice;
    let discount = 0;
    let discountPercentage = 0;

    if (adjustment.value_type === 'percentage') {
      discountPercentage = adjustment.value;
      discount = Math.round((basePrice * adjustment.value) / 100);
      finalPrice = basePrice - discount;
    } else if (adjustment.value_type === 'fixed_amount') {
      discount = adjustment.value;
      finalPrice = basePrice - discount;
      discountPercentage = Math.round((discount / basePrice) * 100);
    }

    return {
      price: finalPrice,
      compareAtPrice: basePrice,
      discount: discount,
      discountPercentage: discountPercentage,
      savings: discount
    };
  }

  /**
   * Format price for display
   * @param {number} priceInCents - Price in cents
   * @param {string} currencySymbol - Currency symbol (default: $)
   * @returns {string} Formatted price
   */
  formatPrice(priceInCents, currencySymbol = '$') {
    const priceInDollars = (priceInCents / 100).toFixed(2);
    return `${currencySymbol}${priceInDollars}`;
  }

  /**
   * Get subscription frequency text
   * @param {Object} sellingPlan - Selling plan object
   * @returns {string} Frequency text (e.g., "every 2 weeks")
   */
  getFrequencyText(sellingPlan) {
    if (!sellingPlan || !sellingPlan.billing_policy) {
      return '';
    }

    const interval = sellingPlan.billing_policy.interval;
    const intervalCount = sellingPlan.billing_policy.interval_count;

    const intervalText = intervalCount === 1
      ? interval.toLowerCase()
      : `${intervalCount} ${interval.toLowerCase()}s`;

    return `every ${intervalText}`;
  }

  /**
   * Add product to cart with subscription
   * @param {Object} params - Cart addition parameters
   * @param {string|number} params.variantId - Variant ID
   * @param {number} params.quantity - Quantity
   * @param {string} params.sellingPlanId - Selling plan ID (optional)
   * @param {Object} params.properties - Line item properties (optional)
   * @returns {Promise<Object>} Cart response
   */
  async addToCart(params) {
    const { variantId, quantity = 1, sellingPlanId, properties = {} } = params;

    const data = {
      items: [{
        id: variantId,
        quantity: quantity,
        properties: properties
      }]
    };

    // Add selling plan if provided
    if (sellingPlanId) {
      data.items[0].selling_plan = sellingPlanId;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('recharge:added-to-cart', {
        detail: {
          variantId: variantId,
          quantity: quantity,
          sellingPlanId: sellingPlanId,
          result: result
        }
      }));

      return result;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Update cart line item with subscription
   * @param {Object} params - Update parameters
   * @param {number} params.line - Cart line number (1-based)
   * @param {number} params.quantity - New quantity
   * @param {string} params.sellingPlanId - Selling plan ID (optional)
   * @returns {Promise<Object>} Updated cart
   */
  async updateCartLine(params) {
    const { line, quantity, sellingPlanId } = params;

    const data = {
      line: line,
      quantity: quantity
    };

    if (sellingPlanId) {
      data.selling_plan = sellingPlanId;
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  }

  /**
   * Get current cart
   * @returns {Promise<Object>} Cart object
   */
  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      throw error;
    }
  }
}

// Create global instance
window.rechargeAPI = new RechargeSubscriptionAPI();

// Auto-initialize if settings are available
if (typeof Shopify !== 'undefined' && Shopify.shop) {
  const storefrontToken = document.querySelector('[data-storefront-token]')?.dataset.storefrontToken;

  if (storefrontToken) {
    window.rechargeAPI.init({
      storeIdentifier: Shopify.shop,
      storefrontAccessToken: storefrontToken,
      appName: 'subscription-widget',
      appVersion: '1.0.0'
    });
  }
}

